import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoriesApi } from '../../api';
import TagInput from '../common/TagInput';
import { Icon } from '../common/Icons';

const STORAGE_KEY = 'memora_search_state';

function loadPersistedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {
    searchType: 'notes',
    noteTitle: '',
    selectedCategoryId: '',
    noteTags: [],
    categoryName: '',
  };
}

export default function SearchModal({ onClose }) {
  const navigate = useNavigate();
  const persisted = loadPersistedState();

  const [searchType, setSearchType] = useState(persisted.searchType === 'folders' ? 'categories' : (persisted.searchType || 'notes'));
  const [noteTitle, setNoteTitle] = useState(persisted.noteTitle || '');
  const [selectedCategoryId, setSelectedCategoryId] = useState(persisted.selectedCategoryId || persisted.selectedFolderId || '');
  const [noteTags, setNoteTags] = useState(Array.isArray(persisted.noteTags) ? persisted.noteTags : []);
  const [categoryName, setCategoryName] = useState(persisted.categoryName || persisted.folderName || '');

  const [categoriesList, setCategoriesList] = useState([]);
  const [validationError, setValidationError] = useState('');

  // Custom Category Picker state
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const categoryPickerRef = useRef(null);

  // Persist state updates to localStorage
  const syncStorage = (updates = {}) => {
    try {
      const state = {
        searchType,
        noteTitle,
        selectedCategoryId,
        noteTags,
        categoryName,
        ...updates,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {}
  };

  // Fetch available categories for dropdown
  useEffect(() => {
    categoriesApi.list()
      .then(r => setCategoriesList(r.data))
      .catch(() => {});
  }, []);

  // Click outside to close category picker
  useEffect(() => {
    if (!categoryPickerOpen) return;
    const handleClickOutside = (e) => {
      if (categoryPickerRef.current && !categoryPickerRef.current.contains(e.target)) {
        setCategoryPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [categoryPickerOpen]);

  const selectedCategoryDisplayName = useMemo(() => {
    if (!selectedCategoryId) return 'All Categories';
    const found = categoriesList.find(c => String(c.id) === String(selectedCategoryId));
    return found ? found.name : 'All Categories';
  }, [selectedCategoryId, categoriesList]);

  const filteredCategoriesForPicker = useMemo(() => {
    if (!categorySearchQuery.trim()) return categoriesList;
    const q = categorySearchQuery.toLowerCase();
    return categoriesList.filter(c => c.name.toLowerCase().includes(q));
  }, [categoriesList, categorySearchQuery]);

  // Keyboard shortcut: Esc to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleClear = () => {
    if (searchType === 'notes') {
      setNoteTitle('');
      setSelectedCategoryId('');
      setNoteTags([]);
      syncStorage({ noteTitle: '', selectedCategoryId: '', noteTags: [] });
    } else {
      setCategoryName('');
      syncStorage({ categoryName: '' });
    }
    setValidationError('');
  };

  const handleNotesSearch = (e) => {
    e?.preventDefault();
    setValidationError('');

    const cleanTitle = noteTitle.trim();
    const hasTags = noteTags.length > 0;

    if (!cleanTitle && !hasTags && !selectedCategoryId) {
      setValidationError('Please enter a note title, select a category, or add hashtags to search.');
      return;
    }

    syncStorage();
    onClose();

    const params = new URLSearchParams();
    params.set('type', 'notes');
    if (cleanTitle) params.set('title', cleanTitle);
    if (selectedCategoryId) {
      params.set('categoryId', selectedCategoryId);
      params.set('folderId', selectedCategoryId); // for backwards compatibility
    }
    if (hasTags) params.set('tags', noteTags.join(','));

    navigate(`/search?${params.toString()}`);
  };

  const handleCategorySearch = (e) => {
    e?.preventDefault();
    setValidationError('');

    const cleanName = categoryName.trim();
    if (!cleanName) {
      setValidationError('Please enter a category name to search.');
      return;
    }

    syncStorage();
    onClose();

    const params = new URLSearchParams();
    params.set('type', 'categories');
    params.set('name', cleanName);

    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="search-backdrop-blur" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="search-modal-centered" role="dialog" aria-modal="true">
        {/* Modal Header */}
        <div className="search-modal-header">
          {/* Unified Slider: Notes Search on left, Category Search on right */}
          <div className="search-slider-toggle">
            <div className={`search-slider-pill ${searchType === 'categories' ? 'categories' : 'notes'}`} />
            <button
              type="button"
              className={`search-slider-btn ${searchType === 'notes' ? 'active' : ''}`}
              onClick={() => {
                setSearchType('notes');
                syncStorage({ searchType: 'notes' });
                setValidationError('');
              }}
            >
              <Icon name="note" size={15} />
              <span>Notes Search</span>
            </button>
            <button
              type="button"
              className={`search-slider-btn ${searchType === 'categories' ? 'active' : ''}`}
              onClick={() => {
                setSearchType('categories');
                syncStorage({ searchType: 'categories' });
                setValidationError('');
              }}
            >
              <Icon name="category" size={15} />
              <span>Category Search</span>
            </button>
          </div>

          <button
            type="button"
            className="btn-icon search-modal-close"
            onClick={onClose}
            aria-label="Close search"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* Validation Alert */}
        {validationError && (
          <div className="alert-custom error search-validation-banner">
            <Icon name="alert-triangle" size={15} />
            <span>{validationError}</span>
          </div>
        )}

        {/* Search Input Forms with Proper Vertical Spacing */}
        <div className="search-modal-body">
          {searchType === 'notes' ? (
            <form onSubmit={handleNotesSearch} className="search-inputs-stack">
              {/* Note Title */}
              <div className="form-group">
                <label className="form-label" htmlFor="modal-search-title">
                  Title
                </label>
                <div className="input-with-icon-wrap">
                  <Icon name="note" size={16} className="input-leading-icon" />
                  <input
                    id="modal-search-title"
                    className="form-input with-leading-icon"
                    placeholder="e.g. Meeting notes, Ideas, Architecture..."
                    value={noteTitle}
                    onChange={e => {
                      setNoteTitle(e.target.value);
                      syncStorage({ noteTitle: e.target.value });
                      if (validationError) setValidationError('');
                    }}
                    autoFocus
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="form-group">
                <label className="form-label">
                  Category
                </label>
                <div className="custom-folder-picker-wrapper" ref={categoryPickerRef}>
                  <button
                    type="button"
                    className={`custom-folder-picker-trigger ${categoryPickerOpen ? 'open' : ''}`}
                    onClick={() => setCategoryPickerOpen(prev => !prev)}
                  >
                    <div className="folder-picker-current">
                      <Icon name="category" size={16} className="folder-picker-icon" />
                      <span>{selectedCategoryDisplayName}</span>
                      {selectedCategoryDisplayName === 'General' && (
                        <span className="category-default-chip-grey">Default</span>
                      )}
                    </div>
                    <Icon name={categoryPickerOpen ? 'chevron-up' : 'chevron-down'} size={15} className="folder-picker-arrow" />
                  </button>

                  {categoryPickerOpen && (
                    <div className="custom-folder-picker-menu">
                      <div className="folder-picker-search-bar">
                        <Icon name="search" size={14} className="folder-picker-search-icon" />
                        <input
                          className="folder-picker-search-input"
                          placeholder="Search categories..."
                          value={categorySearchQuery}
                          onChange={e => setCategorySearchQuery(e.target.value)}
                          autoFocus
                        />
                        {categorySearchQuery && (
                          <button
                            type="button"
                            className="folder-picker-clear-search"
                            onClick={() => setCategorySearchQuery('')}
                          >
                            <Icon name="close" size={12} />
                          </button>
                        )}
                      </div>

                      <div className="folder-picker-options-list">
                        {/* All Categories Option */}
                        <div
                          className={`folder-picker-option ${!selectedCategoryId ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedCategoryId('');
                            syncStorage({ selectedCategoryId: '' });
                            setCategoryPickerOpen(false);
                          }}
                        >
                          <div className="folder-picker-option-left">
                            <Icon name="category" size={15} />
                            <span className="folder-picker-option-name">All Categories</span>
                          </div>
                          {!selectedCategoryId && <Icon name="check" size={15} className="folder-picker-check" />}
                        </div>

                        {/* Category options without note count */}
                        {filteredCategoriesForPicker.map(c => {
                          const isSelected = String(selectedCategoryId) === String(c.id);
                          const isDefault = c.name === 'General' || c.name === 'My Folder';
                          return (
                            <div
                              key={c.id}
                              className={`folder-picker-option ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                setSelectedCategoryId(c.id);
                                syncStorage({ selectedCategoryId: c.id });
                                setCategoryPickerOpen(false);
                              }}
                            >
                              <div className="folder-picker-option-left">
                                <Icon name="category" size={15} />
                                <span className="folder-picker-option-name">{c.name}</span>
                                {isDefault && <span className="category-default-chip-grey">Default</span>}
                              </div>
                              {isSelected && <Icon name="check" size={15} className="folder-picker-check" />}
                            </div>
                          );
                        })}

                        {filteredCategoriesForPicker.length === 0 && categorySearchQuery && (
                          <div className="folder-picker-no-results">
                            No categories found matching "{categorySearchQuery}"
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Hashtags Filter */}
              <div className="form-group">
                <label className="form-label">
                  Filter by Hashtags <span className="label-hint">(Optional)</span>
                </label>
                <TagInput
                  tags={noteTags}
                  onChange={tags => {
                    setNoteTags(tags);
                    syncStorage({ noteTags: tags });
                    if (validationError) setValidationError('');
                  }}
                  placeholder="Type hashtag and press Enter or Tab..."
                />
              </div>

              {/* Action Buttons: Clear + Search Notes */}
              <div className="search-action-row">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={handleClear}
                >
                  <Icon name="rotate-ccw" size={14} />
                  <span>Clear</span>
                </button>

                <button
                  type="submit"
                  className="btn-primary-custom"
                >
                  <Icon name="search" size={16} />
                  <span>Search</span>
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleCategorySearch} className="search-inputs-stack">
              {/* Category Name */}
              <div className="form-group">
                <label className="form-label" htmlFor="modal-search-category-name">
                  Name
                </label>
                <div className="input-with-icon-wrap">
                  <Icon name="category" size={16} className="input-leading-icon" />
                  <input
                    id="modal-search-category-name"
                    className="form-input with-leading-icon"
                    placeholder="e.g. Work, Finance, Ideas..."
                    value={categoryName}
                    onChange={e => {
                      setCategoryName(e.target.value);
                      syncStorage({ categoryName: e.target.value });
                      if (validationError) setValidationError('');
                    }}
                    autoFocus
                  />
                </div>
              </div>

              {/* Action Buttons: Clear + Search Categories */}
              <div className="search-action-row">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={handleClear}
                >
                  <Icon name="rotate-ccw" size={14} />
                  <span>Clear</span>
                </button>

                <button
                  type="submit"
                  className="btn-primary-custom"
                >
                  <Icon name="search" size={16} />
                  <span>Search</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
